import { defineStore } from 'pinia';
import { Entity } from './Entity';
import { useApiStore } from './useApiStore';
import { EntityLockResult, EntityLockResultType, LockedEntityType } from '../interfaces/types.analytics';
import { BillSearchedWithConnections, BillLinkedBase, ProjectInBillConnection, StorageItemMovementInFinances } from '../../models/interfaces/finances/finance-models';
import { i18n } from '../../i18n';

export type LockStatusValue = 'locked' | 'pending' | 'locked_by_other' | 'error';

let lockWorker: Worker | null = null;
let retryIntervalId: ReturnType<typeof setInterval> | null = null;
const RETRY_INTERVAL_MS = 10000;

export enum BillRowProps {
  BillSearchedBase = 0,
  BillLinkedBase = 1,
  BillInfo = 2,
  PaymentInfo = 3
}

export interface ConnectionLine {
  conn_id: number;

  bill?: BillSearchedWithConnections | BillLinkedBase;
  project?: ProjectInBillConnection;
  movement?: StorageItemMovementInFinances;
  percent: number;
  amount: number;
  debit: number;
  maxAmount?: number; // max amount for the percent calculation
  limitAmount?: number; // max amount for the field
  maxPercent?: number;
  maxDebit?: number;
  billRowType?: BillRowProps,

  canEditAmounts: boolean;
  canEditDebits: boolean;
  canBeDeleted: boolean;

  // in case the ConnectionLine[] array is inside the BillInfo component this 
  // means the new connection that represents non-existent income yet. Should have a bill
  nonExistent?: boolean;
  blocked_by_other?: boolean;
}

function getWorker() {
  if (!lockWorker) {
    lockWorker = new Worker(new URL('./lockWorker.ts', import.meta.url), {
      type: 'module'
    });

    const api = useApiStore();

    lockWorker.onmessage = async (ev) => {
      const { event, entity } = ev.data;

      if (event === 'prolong') {
        const store = useEntityStore();
        try {
          const locked = await api.prolongLock(entity.id, entity.type);
          const result = locked as EntityLockResult;
          if (result.Type === EntityLockResultType.LockedByOther || (locked as { response_ok?: boolean }).response_ok === false) {
            store.handleProlongFailed(entity.id, entity.type);
            return { ok: false, error: i18n.global.t('entity_locked_other_user') };
          }
        } catch (e) {
          console.error(i18n.global.t('error_extending_lock'), e);
          store.handleProlongFailed(entity.id, entity.type);
        }
      }
    };
  }
  return lockWorker;
}

export const useEntityStore = defineStore('entities', {
  state: () => ({
    items: [] as Entity[],
    map: {} as Record<string, Entity>,
    accounts: [] as ConnectionLine[],
    lockStatus: {} as Record<string, LockStatusValue>,
    retryQueue: {} as Record<string, { id: number; type: LockedEntityType }>,
  }),

  getters: {
    getLockStatus: (state): ((id: number, type: LockedEntityType) => LockStatusValue | undefined) =>
      (id: number, type: LockedEntityType) => state.lockStatus[id + '_' + type],
  },

  actions: {
    /**
     * UI-friendly: возвращает {ok, entity?, error?}
     */
    async addEntity(entity: Entity) {
      const api = useApiStore();
      const e_id = entity.id + '_' + entity.type;

      const locked = await api.acquireLock(entity.id, entity.type);
      const asResult = locked as EntityLockResult;
      const asAny = locked as { response_ok?: boolean; Type?: number };

      const isLockedByUs = asResult.Type === EntityLockResultType.LockedByYou;
      const isLockedByOtherOrNotLocked =
        asResult.Type === EntityLockResultType.LockedByOther ||
        asResult.Type === EntityLockResultType.NotLocked;
      const isError = asAny.response_ok === false || (!asResult.Type && asAny.response_ok !== true);

      if (!isLockedByUs && (isLockedByOtherOrNotLocked || isError)) {
        this.lockStatus[e_id] = isLockedByOtherOrNotLocked ? 'locked_by_other' : 'error';
        this.retryQueue[e_id] = { id: entity.id, type: entity.type };
        this.startRetryLoop();
        return {
          ok: false,
          error: i18n.global.t('entity_locked_or_other_reason')
        };
      }

      this.items.push(entity);
      this.map[e_id] = entity;
      this.lockStatus[e_id] = 'locked';
      delete this.retryQueue[e_id];

      getWorker().postMessage({
        cmd: 'add',
        id: e_id,
        entity,
        interval: 15000
      });

      return { ok: true, entity };
    },

    handleProlongFailed(id: number, type: LockedEntityType) {
      const e_id = id + '_' + type;
      this.lockStatus[e_id] = 'locked_by_other';
      const entity = this.map[e_id];
      if (entity) {
        this.items = this.items.filter((e) => !(e.id === id && e.type === type));
        delete this.map[e_id];
        getWorker().postMessage({ cmd: 'remove', id: e_id });
      }
    },

    startRetryLoop() {
      if (retryIntervalId !== null) return;
      retryIntervalId = setInterval(() => {
        const api = useApiStore();
        const entries = Object.entries(this.retryQueue);
        if (entries.length === 0) {
          if (retryIntervalId !== null) {
            clearInterval(retryIntervalId);
            retryIntervalId = null;
          }
          return;
        }
        entries.forEach(async ([e_id, { id, type }]) => {
          const locked = await api.acquireLock(id, type);
          const asResult = locked as EntityLockResult;
          if (asResult.Type === EntityLockResultType.LockedByYou) {
            const entity: Entity = { id, type, title: '' };
            this.items.push(entity);
            this.map[e_id] = entity;
            this.lockStatus[e_id] = 'locked';
            delete this.retryQueue[e_id];
            getWorker().postMessage({ cmd: 'add', id: e_id, entity, interval: 15000 });
          }
        });
      }, RETRY_INTERVAL_MS);
    },

    /** Быстрый доступ по ID (O(1)) */
    getEntity(id: number, type: number) {
      const e_id = id+'_'+type;
      return this.map[e_id];
    },

    /** Проверка: есть ли в кэше */
    hasEntity(id: number, type: number) {
      const e_id = id+'_'+type;
      return !!this.map[e_id];
    },

    /** Удаление из кэша; безопасно при любом статусе блокировки */
    async removeEntity(id: number, type: number) {
      const e_id = id + '_' + type;
      const entity = this.map[e_id];

      if (entity) {
        this.items = this.items.filter((e) => !(e.id === id && e.type === type));
        delete this.map[e_id];
        const api = useApiStore();
        try {
          await api.finishLock(entity.id, entity.type);
        } catch (_) {
          // ignore
        }
        getWorker().postMessage({ cmd: 'remove', id: e_id });
      }

      delete this.lockStatus[e_id];
      delete this.retryQueue[e_id];
      return true;
    },

    /** Полная очистка */
    clearAll() {
      if (retryIntervalId !== null) {
        clearInterval(retryIntervalId);
        retryIntervalId = null;
      }
      this.retryQueue = {};
      const api = useApiStore();
      this.items.forEach((entity) => {
        try {
          api.finishLock(entity.id, entity.type);
        } catch (_) {
          // ignore
        }
      });
      this.items = [];
      this.map = {};
      this.lockStatus = {};
      getWorker().postMessage({ cmd: 'clear' });
    }
  }
});
